import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/pages/LegalPage";
import { LEGAL_OWNER } from "@/lib/legal";

const TOC = [
  { id: "titular", label: "Identificación del titular" },
  { id: "objeto", label: "Objeto y alcance" },
  { id: "aceptacion", label: "Aceptación" },
  { id: "requisitos", label: "Requisitos para comprar" },
  { id: "productos", label: "Productos digitales" },
  { id: "tecnico", label: "Formatos y requisitos técnicos" },
  { id: "precios", label: "Precios y monedas" },
  { id: "pagos", label: "Procesadores de pago" },
  { id: "pedido", label: "Confirmación y aprobación" },
  { id: "entrega", label: "Entrega digital" },
  { id: "cuenta", label: "Cuenta, acceso y seguridad" },
  { id: "redescarga", label: "Mis compras y re-descargas" },
  { id: "cupones", label: "Cupones y promociones" },
  { id: "licencia", label: "Licencia comercial" },
  { id: "actualizaciones", label: "Actualizaciones y soporte" },
  { id: "reembolsos", label: "Reembolsos y revocación" },
  { id: "propiedad", label: "Propiedad intelectual" },
  { id: "prohibidos", label: "Usos prohibidos" },
  { id: "fraude", label: "Fraude y suspensión" },
  { id: "disponibilidad", label: "Disponibilidad del servicio" },
  { id: "terceros", label: "Servicios de terceros" },
  { id: "responsabilidad", label: "Limitación de responsabilidad" },
  { id: "ley", label: "Ley aplicable y jurisdicción" },
  { id: "cambios", label: "Modificaciones y contacto" },
];

export const Route = createFileRoute("/terminos")({
  head: () => ({
    meta: [
      { title: "Términos y condiciones — Yustiz Haro Resources" },
      {
        name: "description",
        content:
          "Condiciones de compra de los productos digitales de Yustiz Haro Resources: pagos en ARS y USD, entrega digital, cuenta, licencia, soporte y derechos del consumidor.",
      },
      { property: "og:title", content: "Términos y condiciones — Yustiz Haro Resources" },
      {
        property: "og:description",
        content: "Condiciones de compra de productos digitales descargables.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recursos.yustizharo.com/terminos" },
    ],
    links: [{ rel: "canonical", href: "https://recursos.yustizharo.com/terminos" }],
  }),
  component: TerminosPage,
});

function TerminosPage() {
  return (
    <LegalPage
      title="Términos y condiciones"
      currentPath="/terminos"
      toc={TOC}
      intro={
        <p>
          Estos términos regulan la compra y el uso de los productos digitales ofrecidos en{" "}
          {LEGAL_OWNER.site}.
        </p>
      }
    >
      <h2 id="titular">1. Identificación del titular</h2>
      <p>
        El sitio {LEGAL_OWNER.site} opera bajo el nombre comercial {LEGAL_OWNER.brand} y es
        titularidad de {LEGAL_OWNER.holder}, CUIT {LEGAL_OWNER.cuit}, con domicilio en{" "}
        {LEGAL_OWNER.address}. Contacto legal y de soporte:{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>.
      </p>

      <h2 id="objeto">2. Objeto y alcance</h2>
      <p>
        La actividad consiste en la comercialización online de productos digitales descargables
        para edición audiovisual: colecciones de efectos, overlays, transiciones, LUTs,
        texturas, plantillas y packs que agrupan varias colecciones.
      </p>

      <h2 id="aceptacion">3. Aceptación de los términos</h2>
      <p>
        Al navegar el sitio, crear una cuenta o completar una compra aceptás estos términos, la{" "}
        <Link to="/licencia">Licencia de uso</Link>, la{" "}
        <Link to="/privacidad">Política de privacidad</Link> y la{" "}
        <Link to="/politica-de-reembolsos">Política de reembolsos</Link>. Si no estás de acuerdo,
        no debés realizar la compra.
      </p>

      <h2 id="requisitos">4. Requisitos para comprar y datos informados</h2>
      <p>
        Para comprar es necesario tener capacidad legal para contratar e informar datos veraces,
        completos y actualizados. El comprador es responsable de la exactitud de la información
        que proporciona, en particular de su dirección de email, que es la clave de acceso a los
        archivos adquiridos.
      </p>

      <h2 id="productos">5. Productos exclusivamente digitales</h2>
      <p>
        Todos los productos son digitales y descargables. No existe envío físico, soporte
        material ni costo de envío. La entrega se realiza de forma electrónica a través de la
        biblioteca privada de compras del sitio.
      </p>

      <h2 id="tecnico">6. Información de productos, formatos y compatibilidad</h2>
      <p>
        Cada página de producto detalla el contenido incluido, la cantidad de archivos y los
        formatos entregados. El comprador es responsable de contar con el software y el hardware
        necesarios para utilizar los archivos. Antes de comprar podés consultar cualquier duda
        de compatibilidad escribiendo a{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>.
      </p>

      <h2 id="precios">7. Precios y monedas</h2>
      <p>
        Los precios se muestran en pesos argentinos (ARS) para la región Argentina y en dólares
        estadounidenses (USD) para la región Internacional, según el selector de región del
        sitio. El precio aplicable es el vigente al momento de confirmar el pedido. Los precios
        pueden actualizarse; las compras ya realizadas conservan su acceso a lo adquirido.
      </p>

      <h2 id="pagos">8. Mercado Pago y PayPal</h2>
      <p>
        Los pagos se procesan a través de Mercado Pago (pesos argentinos) y PayPal (dólares
        estadounidenses). Ambos son procesadores independientes con sus propios términos y
        políticas de privacidad. {LEGAL_OWNER.brand} no accede ni almacena los datos completos
        de tarjetas o cuentas utilizados en esas plataformas.
      </p>

      <h2 id="pedido">9. Confirmación del pedido y aprobación del pago</h2>
      <p>
        Al finalizar el checkout se genera un pedido con un identificador propio y se registra
        electrónicamente. El contrato se perfecciona cuando el procesador de pago informa la
        aprobación de la operación. Mientras el pago figure pendiente, el pedido queda registrado
        pero el acceso a los archivos no se habilita.
      </p>

      <h2 id="entrega">10. Entrega digital y email asociado</h2>
      <p>
        Una vez aprobado el pago, el acceso se habilita automáticamente y queda asociado al email
        informado en la compra. Se envían comunicaciones de confirmación y de entrega a ese email.
        Si el email fue escrito con un error, el comprador debe informarlo a soporte para
        corregir la asociación del pedido.
      </p>

      <h2 id="cuenta">11. Cuenta, acceso con Google y seguridad</h2>
      <p>
        El acceso a la biblioteca privada se realiza con una cuenta vinculada al email de la
        compra. Se puede ingresar con Google o con email y contraseña, y también solicitar un
        enlace de acceso enviado por email para quienes compraron sin crear contraseña. El
        comprador es responsable de mantener la confidencialidad de sus credenciales y del
        control de su casilla de correo.
      </p>

      <h2 id="redescarga">12. Mis compras y re-descargas</h2>
      <p>
        Los archivos se descargan mediante enlaces firmados y temporales generados en el momento
        de la descarga. Podés volver a generar esos enlaces desde la sección de compras cuando lo
        necesites, mientras el producto esté disponible en la plataforma y el acceso no haya sido
        revocado por reembolso o incumplimiento.
      </p>

      <h2 id="cupones">13. Cupones y promociones</h2>
      <p>
        Los cupones y promociones que se publiquen tienen las condiciones, vigencia y
        limitaciones que se informen en cada caso. No son acumulables salvo indicación expresa,
        no son canjeables por dinero y no se aplican retroactivamente a compras ya finalizadas.
      </p>

      <h2 id="licencia">14. Licencia comercial aplicable</h2>
      <p>
        El uso de los recursos se rige por la <Link to="/licencia">Licencia de uso comercial</Link>:
        perpetua, mundial, no exclusiva e intransferible, válida para proyectos propios y de
        clientes, con prohibición de reventa y redistribución de los archivos originales.
      </p>

      <h2 id="actualizaciones">15. Actualizaciones de productos y soporte</h2>
      <p>
        Las actualizaciones publicadas sobre un producto adquirido se incorporan sin costo a la
        biblioteca del comprador. El soporte cubre problemas de compra, acceso, descarga e
        integridad de los archivos, y se brinda por email a{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a> o desde la{" "}
        <Link to="/contacto">página de contacto</Link>. El soporte no incluye formación
        personalizada sobre software de edición.
      </p>

      <h2 id="reembolsos">16. Reembolsos y revocación de acceso</h2>
      <p>
        Las condiciones de reembolso se detallan en la{" "}
        <Link to="/politica-de-reembolsos">Política de reembolsos</Link>. También podés iniciar
        una solicitud sin registrarte desde el{" "}
        <Link to="/arrepentimiento">Botón de arrepentimiento</Link>. Cuando se aprueba un
        reembolso, o cuando el procesador informa una cancelación, devolución o contracargo, el
        acceso al producto se revoca y las descargas dejan de estar disponibles.
      </p>

      <h2 id="propiedad">17. Propiedad intelectual</h2>
      <p>
        Los archivos, textos, imágenes, videos, marcas y elementos gráficos del sitio están
        protegidos por la normativa de propiedad intelectual y pertenecen al titular o a sus
        licenciantes. La compra otorga una licencia de uso y no transfiere titularidad alguna.
      </p>

      <h2 id="prohibidos">18. Usos prohibidos</h2>
      <ul>
        <li>Compartir, revender o redistribuir los archivos o los enlaces de descarga.</li>
        <li>Compartir credenciales o el acceso a la biblioteca privada.</li>
        <li>
          Intentar vulnerar la seguridad del sitio, automatizar descargas masivas o eludir los
          controles de acceso.
        </li>
        <li>Utilizar el sitio con fines fraudulentos o contrarios a la ley.</li>
      </ul>

      <h2 id="fraude">19. Prevención de fraude y suspensión de cuentas</h2>
      <p>
        Ante indicios razonables de fraude, uso de medios de pago no autorizados o incumplimiento
        grave de estos términos, el titular puede suspender preventivamente el acceso de la
        cuenta involucrada. La suspensión se comunica al email registrado indicando el motivo y
        el comprador puede presentar su descargo por el mismo canal.
      </p>

      <h2 id="disponibilidad">20. Disponibilidad, mantenimiento y cambios</h2>
      <p>
        El sitio puede sufrir interrupciones por mantenimiento, actualizaciones o causas ajenas
        al titular. Se procura minimizar esas interrupciones y restablecer el servicio en el menor
        tiempo posible. El catálogo, los productos y las funcionalidades pueden modificarse de
        forma razonable sin afectar el acceso a lo ya adquirido.
      </p>

      <h2 id="terceros">21. Servicios y enlaces de terceros</h2>
      <p>
        El sitio utiliza servicios de terceros para pagos, autenticación, alojamiento y envío de
        correos, y puede enlazar a sitios externos como yustizharo.com o academy.yustizharo.com.
        Esos servicios se rigen por sus propias condiciones y políticas, sobre las que el titular
        no tiene control.
      </p>

      <h2 id="responsabilidad">22. Limitación de responsabilidad</h2>
      <p>
        El titular responde por el correcto suministro de los archivos adquiridos y por los daños
        directos que le sean imputables. No responde por resultados creativos o comerciales
        esperados por el comprador, ni por fallas de software de terceros, del equipo del usuario
        o de la conexión a internet. Nada de lo previsto en estos términos limita o excluye los
        derechos que la normativa argentina de defensa del consumidor reconoce de forma
        imperativa a los consumidores.
      </p>

      <h2 id="ley">23. Ley aplicable y jurisdicción</h2>
      <p>
        Estos términos se rigen por las leyes de la {LEGAL_OWNER.law}, incluidas la Ley 24.240 de
        Defensa del Consumidor y el Código Civil y Comercial de la Nación. Para las relaciones de
        consumo resulta competente el tribunal que corresponda conforme a las normas imperativas
        aplicables, sin que estos términos impongan una jurisdicción exclusiva que impida al
        consumidor reclamar ante los tribunales de su domicilio. Los consumidores también pueden
        recurrir al Servicio de Conciliación Previa en las Relaciones de Consumo (COPREC) y a las
        autoridades de defensa del consumidor.
      </p>

      <h2 id="cambios">24. Modificaciones y contacto</h2>
      <p>
        Estos términos pueden actualizarse para reflejar cambios legales u operativos. La versión
        vigente es siempre la publicada en esta página, con su fecha de actualización. Consultas:{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>.
      </p>
    </LegalPage>
  );
}
